/* eslint-disable @typescript-eslint/no-unused-vars */
// src/pages/ContactSupport.tsx
import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import {Helmet} from "react-helmet-async";
import { useDarkMode } from "../context/DarkModeContext";
import {
  Mail,
  Phone,
  MessageCircle,
  MapPin,
  Clock,
  CheckCircle,
  AlertCircle,
  Send,
  Loader2,
  ArrowRight,
  HelpCircle,
  Users,
  BookOpen,
} from "lucide-react";

const ContactSupport: React.FC = () => {
  const { isDark } = useDarkMode();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    category: "general",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<
    "idle" | "success" | "error"
  >("idle");

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus("idle");

    try {
      // Use the environment variable
      const API_BASE = import.meta.env.VITE_API_BASE || "";
      const response = await fetch(`${API_BASE}/contacts/submit`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });

      // Check if response has content before parsing JSON
      const contentType = response.headers.get("content-type");
      let result;

      if (contentType && contentType.includes("application/json")) {
        result = await response.json();
      } else {
        // Handle non-JSON response (like HTML error pages)
        const text = await response.text();
        throw new Error(
          `Server returned ${response.status}: ${text.substring(0, 100)}`,
        );
      }

      if (response.ok) {
        setSubmitStatus("success");
        setFormData({
          name: "",
          email: "",
          subject: "",
          category: "general",
          message: "",
        });
      } else {
        setSubmitStatus("error");
        console.error("Submission failed:", result.error);
      }
    } catch (error) {
      setSubmitStatus("error");
      console.error("Submission error:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleNavigateToFAQ = () => {
    navigate("/faq");
  };

  // const handleNavigateToHelpCenter = () => {
  //   navigate("/help-center");
  // };

  const handleNavigateToCommunity = () => {
    navigate("/community");
  };

  const contactMethods = [
    {
      icon: Mail,
      title: "Email Support",
      description: "Send us an email and we'll respond within 24 hours",
      contact: "findworkenquiries@gmail.com",
      link: "mailto:findworkenquiries@gmail.com",
    },
    {
      icon: Phone,
      title: "Phone Support",
      description: "Call us during business hours for immediate assistance",
      contact: "+254 758337470",
      link: "tel:+254758337470",
    },
    {
      icon: MessageCircle,
      title: "Live Chat",
      description: "Chat with our support team in real-time",
      contact: "Available 9AM-6PM EST",
      link: "#live-chat",
    },
    {
      icon: MapPin,
      title: "Office Address",
      description: "Visit our headquarters during business hours",
      contact: "123 Career Street, Nairobi, Kenya",
      link: "https://maps.google.com",
    },
  ];

  const faqs = [
    {
      question: "How do I reset my password?",
      answer:
        'Click on "Forgot Password" on the login page and follow the instructions sent to your email.',
    },
    {
      question: "Can I update my resume after submitting applications?",
      answer:
        "Yes, you can update your resume at any time. However, applications already submitted will use the resume version at the time of application.",
    },
    {
      question: "How do I cancel my subscription?",
      answer:
        'Go to your account settings, click on "Subscription" and select "Cancel Subscription". Your access will continue until the end of your billing period.',
    },
    {
      question: "Are my job applications confidential?",
      answer:
        "Yes, your applications are only shared with the employers you apply to. We never share your information with third parties without your consent.",
    },
  ];

  return (
    <div
      className={`min-h-screen py-8 px-4 sm:px-6 lg:px-8 transition-colors duration-300 ${
        isDark
          ? 'bg-gray-900 text-gray-100'
          : 'bg-gradient-to-br from-sky-50 to-blue-50 text-gray-900'
      }`}
    >
      <Helmet>
        <title>Contact Support | FindWork Kenya</title>
        <meta
          name="description"
          content="Get in touch with the FindWork support team. We're ready to help with any questions about jobs, payments, or your account."
        />
        <link rel="canonical" href="https://findwork.ltd/contact-support" />
      </Helmet>
       
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <h1
            className={`text-3xl sm:text-4xl font-bold mb-4 bg-gradient-to-r ${
              isDark
                ? 'from-blue-400 to-blue-600 text-white'
                : 'from-sky-500 to-blue-600 text-transparent bg-clip-text'
            }`}
          >
            Contact Support
          </h1>
          <p
            className={`text-lg max-w-2xl mx-auto ${
              isDark ? 'text-gray-400' : 'text-blue-700'
            }`}
          >
            We're here to help you with any questions or issues you may have.
            Get in touch with our support team.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Contact Methods */}
          <div className="lg:col-span-1">
            <div
              className={`rounded-xl shadow-lg p-6 ${
                isDark
                  ? 'bg-gray-800 border border-gray-700'
                  : 'bg-white border border-blue-200 shadow-blue-100'
              }`}
            >
              <h2
                className={`text-2xl font-semibold mb-6 ${
                  isDark ? 'text-white' : 'text-blue-800'
                }`}
              >
                Get in Touch
              </h2>

              <div className="space-y-6">
                {contactMethods.map((method, index) => {
                  const IconComponent = method.icon;
                  return (
                    <div
                      key={index}
                      className={`p-4 rounded-lg transition-all duration-200 ${
                        isDark
                          ? 'bg-gray-700 hover:bg-gray-600'
                          : 'bg-blue-50 hover:bg-blue-100'
                      }`}
                    >
                      <div className="flex items-start space-x-4">
                        <div
                          className={`p-2 rounded-lg ${
                            isDark ? 'bg-blue-600/20' : 'bg-blue-100'
                          }`}
                        >
                          <IconComponent
                            className={`w-5 h-5 ${
                              isDark ? 'text-blue-400' : 'text-blue-600'
                            }`}
                          />
                        </div>
                        <div>
                          <h3
                            className={`font-semibold mb-1 ${
                              isDark ? 'text-white' : 'text-blue-800'
                            }`}
                          >
                            {method.title}
                          </h3>
                          <p
                            className={`text-sm mb-2 ${
                              isDark ? 'text-gray-400' : 'text-blue-700'
                            }`}
                          >
                            {method.description}
                          </p>
                          <a
                            href={method.link}
                            className={`font-medium text-sm flex items-center space-x-1 ${
                              isDark
                                ? 'text-blue-400 hover:text-blue-300'
                                : 'text-blue-600 hover:text-blue-800'
                            }`}
                          >
                            <span>{method.contact}</span>
                          </a>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Response Time */}
              <div
                className={`mt-6 p-4 rounded-lg ${
                  isDark
                    ? 'bg-blue-900/20 border border-blue-800'
                    : 'bg-blue-100 border border-blue-200'
                }`}
              >
                <div className="flex items-center space-x-3">
                  <Clock
                    className={`w-4 h-4 ${
                      isDark ? 'text-green-400' : 'text-green-500'
                    }`}
                  />
                  <div>
                    <p
                      className={`font-medium ${
                        isDark ? 'text-gray-200' : 'text-blue-800'
                      }`}
                    >
                      Average Response Time
                    </p>
                    <p
                      className={`text-sm ${
                        isDark ? 'text-gray-400' : 'text-blue-700'
                      }`}
                    >
                      Less than 4 hours during business hours
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* FAQ Section */}
            <div
              className={`mt-8 rounded-xl shadow-lg p-6 ${
                isDark
                  ? 'bg-gray-800 border border-gray-700'
                  : 'bg-white border border-blue-200 shadow-blue-100'
              }`}
            >
              <h2
                className={`text-2xl font-semibold mb-6 ${
                  isDark ? 'text-white' : 'text-blue-800'
                }`}
              >
                Frequently Asked Questions
              </h2>

              <div className="space-y-4">
                {faqs.map((faq, index) => (
                  <div
                    key={index}
                    className={`p-4 rounded-lg ${
                      isDark ? 'bg-gray-700' : 'bg-blue-50'
                    }`}
                  >
                    <h3
                      className={`font-semibold mb-2 flex items-center space-x-2 ${
                        isDark ? 'text-white' : 'text-blue-800'
                      }`}
                    >
                      <HelpCircle className="w-4 h-4" />
                      <span>{faq.question}</span>
                    </h3>
                    <p
                      className={`text-sm ${
                        isDark ? 'text-gray-400' : 'text-blue-700'
                      }`}
                    >
                      {faq.answer}
                    </p>
                  </div>
                ))}
              </div>

              <div className="mt-6 text-center">
                <button
                  onClick={handleNavigateToFAQ}
                  className={`font-medium text-sm flex items-center justify-center space-x-1 ${
                    isDark
                      ? 'text-blue-400 hover:text-blue-300'
                      : 'text-blue-600 hover:text-blue-800'
                  }`}
                >
                  <span>View all FAQs</span>
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="lg:col-span-2">
            <div
              className={`rounded-xl shadow-lg p-6 ${
                isDark
                  ? 'bg-gray-800 border border-gray-700'
                  : 'bg-white border border-blue-200 shadow-blue-100'
              }`}
            >
              <h2
                className={`text-2xl font-semibold mb-6 ${
                  isDark ? 'text-white' : 'text-blue-800'
                }`}
              >
                Send us a Message
              </h2>

              {submitStatus === 'success' && (
                <div
                  className={`mb-6 p-4 rounded-lg ${
                    isDark
                      ? 'bg-green-900/20 border border-green-800'
                      : 'bg-green-100 border border-green-200'
                  }`}
                >
                  <div className="flex items-center space-x-3">
                    <CheckCircle
                      className={`w-6 h-6 ${
                        isDark ? 'text-green-400' : 'text-green-500'
                      }`}
                    />
                    <div>
                      <p
                        className={`font-medium ${
                          isDark ? 'text-gray-200' : 'text-green-800'
                        }`}
                      >
                        Message Sent Successfully!
                      </p>
                      <p
                        className={`text-sm ${
                          isDark ? 'text-gray-400' : 'text-green-700'
                        }`}
                      >
                        We'll get back to you within 4 hours during business
                        hours.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {submitStatus === 'error' && (
                <div
                  className={`mb-6 p-4 rounded-lg ${
                    isDark
                      ? 'bg-red-900/20 border border-red-800'
                      : 'bg-red-100 border border-red-200'
                  }`}
                >
                  <div className="flex items-center space-x-3">
                    <AlertCircle
                      className={`w-6 h-6 ${
                        isDark ? 'text-red-400' : 'text-red-500'
                      }`}
                    />
                    <p
                      className={`font-medium ${
                        isDark ? 'text-gray-200' : 'text-red-800'
                      }`}
                    >
                      There was an error sending your message. Please try again.
                    </p>
                  </div>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label
                      htmlFor="name"
                      className={`block text-sm font-medium mb-2 ${
                        isDark ? 'text-gray-300' : 'text-blue-800'
                      }`}
                    >
                      Full Name *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      value={formData.name}
                      onChange={handleInputChange}
                      className={`w-full px-4 py-3 rounded-lg border focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors ${
                        isDark
                          ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                          : 'bg-white border-blue-200 text-gray-900 placeholder-blue-400'
                      }`}
                      placeholder="Enter your full name"
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="email"
                      className={`block text-sm font-medium mb-2 ${
                        isDark ? 'text-gray-300' : 'text-blue-800'
                      }`}
                    >
                      Email Address *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      value={formData.email}
                      onChange={handleInputChange}
                      className={`w-full px-4 py-3 rounded-lg border focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors ${
                        isDark
                          ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                          : 'bg-white border-blue-200 text-gray-900 placeholder-blue-400'
                      }`}
                      placeholder="your@email.com"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label
                      htmlFor="subject"
                      className={`block text-sm font-medium mb-2 ${
                        isDark ? 'text-gray-300' : 'text-blue-800'
                      }`}
                    >
                      Subject *
                    </label>
                    <input
                      type="text"
                      id="subject"
                      name="subject"
                      required
                      value={formData.subject}
                      onChange={handleInputChange}
                      className={`w-full px-4 py-3 rounded-lg border focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors ${
                        isDark
                          ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                          : 'bg-white border-blue-200 text-gray-900 placeholder-blue-400'
                      }`}
                      placeholder="Brief description of your issue"
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="category"
                      className={`block text-sm font-medium mb-2 ${
                        isDark ? 'text-gray-300' : 'text-blue-800'
                      }`}
                    >
                      Category *
                    </label>
                    <select
                      id="category"
                      name="category"
                      required
                      value={formData.category}
                      onChange={handleInputChange}
                      className={`w-full px-4 py-3 rounded-lg border focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors ${
                        isDark
                          ? 'bg-gray-700 border-gray-600 text-white'
                          : 'bg-white border-blue-200 text-gray-900'
                      }`}
                    >
                      <option value="general">General Inquiry</option>
                      <option value="technical">Technical Support</option>
                      <option value="billing">Billing & Payments</option>
                      <option value="account">Account Issues</option>
                      <option value="feature">Feature Request</option>
                      <option value="bug">Report a Bug</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label
                    htmlFor="message"
                    className={`block text-sm font-medium mb-2 ${
                      isDark ? 'text-gray-300' : 'text-blue-800'
                    }`}
                  >
                    Message *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    required
                    rows={6}
                    value={formData.message}
                    onChange={handleInputChange}
                    className={`w-full px-4 py-3 rounded-lg border focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors resize-none ${
                      isDark
                        ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                        : 'bg-white border-blue-200 text-gray-900 placeholder-blue-400'
                    }`}
                    placeholder="Please describe your issue or question in detail..."
                  />
                </div>

                <div className="flex items-center justify-between">
                  <p
                    className={`text-sm ${
                      isDark ? 'text-gray-400' : 'text-blue-700'
                    }`}
                  >
                    * Required fields
                  </p>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className={`px-8 py-3 rounded-lg font-semibold transition-all duration-200 flex items-center space-x-2 ${
                      isSubmitting
                        ? 'bg-gray-400 cursor-not-allowed'
                        : isDark
                          ? 'bg-blue-600 hover:bg-blue-700 text-white'
                          : 'bg-gradient-to-r from-sky-500 to-blue-600 hover:from-sky-600 hover:to-blue-700 text-white shadow-lg shadow-blue-200'
                    }`}
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        <span>Sending...</span>
                      </>
                    ) : (
                      <>
                        <Send className="w-5 h-5" />
                        <span>Send Message</span>
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>

            {/* Additional Resources */}
            <div
              className={`mt-8 rounded-xl shadow-lg p-6 ${
                isDark
                  ? 'bg-gray-800 border border-gray-700'
                  : 'bg-white border border-blue-200 shadow-blue-100'
              }`}
            >
              <h2
                className={`text-2xl font-semibold mb-6 ${
                  isDark ? 'text-white' : 'text-blue-800'
                }`}
              >
                Additional Resources
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div
                  className={`p-4 rounded-lg ${
                    isDark ? 'bg-gray-700' : 'bg-blue-50'
                  }`}
                >
                  <div className="flex items-center space-x-3 mb-3">
                    <div
                      className={`p-2 rounded-lg ${
                        isDark ? 'bg-blue-600/20' : 'bg-blue-100'
                      }`}
                    >
                      <BookOpen
                        className={`w-5 h-5 ${
                          isDark ? 'text-blue-400' : 'text-blue-600'
                        }`}
                      />
                    </div>
                    <h3
                      className={`font-semibold ${
                        isDark ? 'text-white' : 'text-blue-800'
                      }`}
                    >
                      Help Center
                    </h3>
                  </div>
                  <p
                    className={`text-sm mb-3 ${
                      isDark ? 'text-gray-400' : 'text-blue-700'
                    }`}
                  >
                    Comprehensive guides and tutorials for using FindWork
                    effectively.
                  </p>
                  <Link
                    to="/help-center"
                    className={`font-medium text-sm flex items-center space-x-1 ${
                      isDark
                        ? 'text-blue-400 hover:text-blue-300'
                        : 'text-blue-600 hover:text-blue-800'
                    }`}
                  >
                    <span>Visit Help Center</span>
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>

                <div
                  className={`p-4 rounded-lg ${
                    isDark ? 'bg-gray-700' : 'bg-blue-50'
                  }`}
                >
                  <div className="flex items-center space-x-3 mb-3">
                    <div
                      className={`p-2 rounded-lg ${
                        isDark ? 'bg-blue-600/20' : 'bg-blue-100'
                      }`}
                    >
                      <Users
                        className={`w-5 h-5 ${
                          isDark ? 'text-blue-400' : 'text-blue-600'
                        }`}
                      />
                    </div>
                    <h3
                      className={`font-semibold ${
                        isDark ? 'text-white' : 'text-blue-800'
                      }`}
                    >
                      Community Forum
                    </h3>
                  </div>
                  <p
                    className={`text-sm mb-3 ${
                      isDark ? 'text-gray-400' : 'text-blue-700'
                    }`}
                  >
                    Connect with other FindWork users and share tips and
                    experiences.
                  </p>
                  <button
                    onClick={handleNavigateToCommunity}
                    className={`font-medium text-sm flex items-center space-x-1 ${
                      isDark
                        ? 'text-blue-400 hover:text-blue-300'
                        : 'text-blue-600 hover:text-blue-800'
                    }`}
                  >
                    <span>Join Community</span>
                    <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactSupport;
